import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group, Stack, Text, Tooltip } from '@mantine/core';
import { noop } from 'lodash';
import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import { MDash } from 'ui-components/components/mdash/mdash';
import { DataDrivenLogo } from '../../../../../../../components/data-driven-logo';
import { InventoryConnectionMenu } from '../../../../../../../inventory-sync/components/inventory-connection-menu/inventory-connection-menu';
import type { ConnectionLogoProps } from '../../../../../../../inventory-sync/hooks/use-connection-logo-props';
import { RelativeTime } from '../relative-time/relative-time';
import { ConnectionSyncStatusCell } from './cells/connection-sync-status-cell';
import { messages } from './connection-sync-table.messages';
import style from './connection-sync-table.module.css';
import { formatSyncConfigSummary } from './format-sync-config-summary';
import { INELIGIBLE_REASON_MESSAGES } from './ineligible-reason-messages';

import type { ProductConnectionSyncRow } from '../../product-inventory-sync-types';

export const COLUMN_KEY = {
  connectionName: 'connectionName',
  connectionStatus: 'connectionStatus',
  lastSyncedAt: 'lastSyncedAt',
  nextSyncQuantity: 'nextSyncQuantity',
  config: 'config',
  actions: 'actions',
} as const;

export type ConnectionSyncColumnKey = (typeof COLUMN_KEY)[keyof typeof COLUMN_KEY];

export type ConnectionSyncColumn = {
  key: ConnectionSyncColumnKey;
  header: string;
  render: (row: ProductConnectionSyncRow) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
};

const CONFIG_SCOPE_GAP = 2;
const LAST_SYNC_GAP = 2;

type BuildColumnsOptions = {
  intl: IntlShape;
  getLogoProps: (apiCode: string) => ConnectionLogoProps;
};

export const buildConnectionSyncColumns = ({ intl, getLogoProps }: BuildColumnsOptions): ConnectionSyncColumn[] => {
  return [
    {
      key: COLUMN_KEY.connectionName,
      header: intl.formatMessage(messages.columnConnectionName),
      sortable: true,
      render: (row) => (
        <Group gap='xs' wrap='nowrap'>
          <DataDrivenLogo {...getLogoProps(row.apiCode)} className={style['connection-icon']} logoType='icon' />
          <Text c={row.ineligibleReason ? 'dimmed' : undefined} size='sm'>
            {row.connectionName}
          </Text>
          {row.ineligibleReason && (
            <Tooltip label={intl.formatMessage(INELIGIBLE_REASON_MESSAGES[row.ineligibleReason])}>
              <span className={style['ineligible-info-icon']}>
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
            </Tooltip>
          )}
        </Group>
      ),
    },
    {
      key: COLUMN_KEY.connectionStatus,
      header: intl.formatMessage(messages.columnStatus),
      render: (row) => <ConnectionSyncStatusCell row={row} />,
    },
    {
      key: COLUMN_KEY.lastSyncedAt,
      header: intl.formatMessage(messages.columnLastSynced),
      render: (row) => (
        <Stack gap={LAST_SYNC_GAP}>
          <Tooltip label={intl.formatMessage(messages.lastSyncedQtyTooltip)}>
            <Text className={style['numeric-cell']} size='sm'>
              {row.lastSyncedQuantity === null ? <MDash /> : intl.formatNumber(row.lastSyncedQuantity)}
            </Text>
          </Tooltip>
          <Text c='dimmed' size='xs'>
            {row.lastSyncedAt ? <RelativeTime timestamp={row.lastSyncedAt} /> : <FormattedMessage {...messages.neverSynced} />}
          </Text>
        </Stack>
      ),
    },
    {
      key: COLUMN_KEY.nextSyncQuantity,
      header: intl.formatMessage(messages.columnNextSync),
      align: 'right',
      render: (row) => (
        <Tooltip label={intl.formatMessage(messages.nextSyncTooltip)}>
          <Text className={style['numeric-cell']} size='sm'>
            {row.nextSyncQuantity === null ? <MDash /> : intl.formatNumber(row.nextSyncQuantity)}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: COLUMN_KEY.config,
      header: intl.formatMessage(messages.columnConfig),
      render: (row) => (
        <Stack gap={CONFIG_SCOPE_GAP}>
          <Text size='sm'>{formatSyncConfigSummary(intl, row.config)}</Text>
          <Text c='dimmed' size='xs'>
            <FormattedMessage {...messages.configScopeConnection} />
          </Text>
        </Stack>
      ),
    },
    {
      key: COLUMN_KEY.actions,
      header: intl.formatMessage(messages.columnActions),
      render: (row) =>
        row.connection ? (
          // readOnly will be lifted in a future PR when per-SKU/connection settings editing is added
          <InventoryConnectionMenu connection={row.connection} onSaveConfig={noop} readOnly />
        ) : null,
    },
  ];
};
